// Geometry for the small SVG charts (ui/chart.tsx bars, ui/line-chart.tsx, ui/pie-chart.tsx): a
// "nice" y scale with labelled ticks, bar slots and stacks, line points and pie arcs. Pure;
// tests/unit/chart-model.test.ts.

export interface Scale {
  max: number;
  step: number;
  ticks: number[];
}

/** A 1-2-2.5-5 step scale from 0 that covers `maxValue` with about `targetTicks` intervals. */
export function niceScale(maxValue: number, targetTicks = 4): Scale {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return { max: 1, step: 1, ticks: [0, 1] };
  const rough = maxValue / Math.max(1, targetTicks);
  const power = 10 ** Math.floor(Math.log10(rough));
  // The smallest 1-2-2.5-5 step that needs at most one interval more than asked for.
  const unit = [1, 2, 2.5, 5, 10].find((c) => Math.ceil(maxValue / (c * power) - 1e-9) <= targetTicks + 1) ?? 10;
  const step = unit * power;
  const max = Math.ceil(maxValue / step - 1e-9) * step;
  const count = Math.round(max / step);
  const ticks = Array.from({ length: count + 1 }, (_, i) => roundTo(i * step, step));
  return { max, step, ticks };
}

function roundTo(value: number, step: number): number {
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 2);
  return Number(value.toFixed(decimals));
}

export interface Plot {
  width: number;
  height: number;
  /** Space for the y tick labels. */
  left: number;
  /** Space for the month labels. */
  bottom: number;
  top: number;
  right: number;
}

export function plotArea(plot: Plot): { x: number; y: number; width: number; height: number } {
  return {
    x: plot.left,
    y: plot.top,
    width: Math.max(0, plot.width - plot.left - plot.right),
    height: Math.max(0, plot.height - plot.top - plot.bottom),
  };
}

/** The y coordinate of a value on the scale, inside the plot area. */
export function yFor(value: number, scale: Scale, area: { y: number; height: number }): number {
  const clamped = Math.max(0, Math.min(value, scale.max));
  return area.y + area.height - (clamped / scale.max) * area.height;
}

export interface BarSlot {
  /** Left edge of the bar. */
  x: number;
  width: number;
  /** Centre of the slot, for the label under it. */
  center: number;
}

/** `count` equal slots across the plot width, each bar `fill` of its slot (capped at `maxBar`). */
export function barSlots(count: number, area: { x: number; width: number }, fill = 0.62, maxBar = 48): BarSlot[] {
  if (count <= 0) return [];
  const slot = area.width / count;
  const width = Math.max(2, Math.min(maxBar, slot * fill));
  return Array.from({ length: count }, (_, i) => {
    const center = area.x + slot * i + slot / 2;
    return { x: center - width / 2, width, center };
  });
}

export interface Segment {
  series: string;
  value: number;
}

export interface StackedRect {
  series: string;
  y: number;
  height: number;
}

/**
 * Stack a bar's segments bottom-up (in the order given). A negative value adds nothing to the
 * stack — a loss is read from the table and the tooltip, never drawn below the axis.
 */
export function stack(segments: readonly Segment[], scale: Scale, area: { y: number; height: number }): StackedRect[] {
  let base = 0;
  const rects: StackedRect[] = [];
  for (const segment of segments) {
    const value = Math.max(0, segment.value);
    const top = base + value;
    const y0 = yFor(base, scale, area);
    const y1 = yFor(top, scale, area);
    rects.push({ series: segment.series, y: y1, height: Math.max(0, y0 - y1) });
    base = top;
  }
  return rects;
}

/** The tallest stack among bars (negative segments count as zero). */
export function maxStack(bars: readonly { segments: readonly Segment[] }[]): number {
  return bars.reduce((max, bar) => Math.max(max, bar.segments.reduce((sum, s) => sum + Math.max(0, s.value), 0)), 0);
}

/** Label every bar when there is room, else every `n`th so labels never collide. */
export function labelEvery(count: number, plotWidth: number, minLabelWidth = 34): number {
  if (count <= 0) return 1;
  const perBar = plotWidth / count;
  return Math.max(1, Math.ceil(minLabelWidth / Math.max(1, perBar)));
}

// ---- line charts (ui/line-chart.tsx) ----

/** `count` x positions spread edge to edge across the plot; one point sits in the middle. */
export function pointXs(count: number, area: { x: number; width: number }): number[] {
  if (count <= 0) return [];
  if (count === 1) return [area.x + area.width / 2];
  const step = area.width / (count - 1);
  return Array.from({ length: count }, (_, i) => area.x + step * i);
}

/** The index of the point nearest an x coordinate (for the hover readout), clamped to the data. */
export function nearestIndex(x: number, xs: readonly number[]): number {
  let best = 0;
  xs.forEach((candidate, index) => {
    if (Math.abs(candidate - x) < Math.abs((xs[best] ?? 0) - x)) best = index;
  });
  return best;
}

/** An SVG path through the points in order ("M x y L x y …"); empty for no points. */
export function linePath(points: readonly { x: number; y: number }[]): string {
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${round2(p.x)} ${round2(p.y)}`).join(" ");
}

/** The fill/stroke class of a series by its position; "other" is always the neutral grey. */
export function seriesClass(id: string, index: number): string {
  if (id === "other") return "series-other";
  return `series-${(index % SERIES_SLOTS) + 1}`;
}

/** The categorical slots styles.css defines (series-1 … series-8); a ninth entity folds into "other". */
export const SERIES_SLOTS = 8;

/** One row of the line chart's readout: a series' value at the hovered x, with its colour slot. */
export interface ReadoutRow {
  id: string;
  label: string;
  value: number;
  /** The series' fill class (seriesClass), for the swatch beside the name. */
  swatch: string;
}

/**
 * Every series' value at point `index`, biggest first so the reader finds the line they are looking
 * at; "other" last whatever its size; a series without that point reads zero. Ties keep the
 * legend's order.
 */
export function readoutRows(series: readonly { id: string; label: string; points: readonly { y: number }[] }[], index: number): ReadoutRow[] {
  const rows = series.map((s, position) => ({ id: s.id, label: s.label, value: s.points[index]?.y ?? 0, swatch: seriesClass(s.id, position) }));
  const named = rows.filter((row) => row.id !== "other").sort((a, b) => b.value - a.value);
  return [...named, ...rows.filter((row) => row.id === "other")];
}

/** The readout as one sentence ("1 Sep 2026: Opus 5.5 50, Nova Pro 5, Other 99."), for the live region. */
export function readoutSummary(x: string, rows: readonly ReadoutRow[], formatValue: (value: number) => string): string {
  if (rows.length === 0) return `${x}: no data.`;
  return `${x}: ${rows.map((row) => `${row.label} ${formatValue(row.value)}`).join(", ")}.`;
}

// ---- pie charts (ui/pie-chart.tsx) ----

export interface PieAngle {
  /** Radians clockwise from 12 o'clock. */
  start: number;
  end: number;
  /** Share of the total, 0..1 (0 for every slice when the total is zero). */
  fraction: number;
}

const TAU = 2 * Math.PI;

/** Consecutive angles for the values; a negative value counts as zero, a zero total draws nothing. */
export function pieAngles(values: readonly number[]): PieAngle[] {
  const clean = values.map((value) => (Number.isFinite(value) ? Math.max(0, value) : 0));
  const total = clean.reduce((sum, value) => sum + value, 0);
  let start = 0;
  return clean.map((value) => {
    const fraction = total > 0 ? value / total : 0;
    const end = start + fraction * TAU;
    const angle = { start, end, fraction };
    start = end;
    return angle;
  });
}

/**
 * The SVG path of a pie slice (a wedge from the centre). An empty sweep is no path; a full turn
 * is a whole circle drawn as two half arcs, since one arc whose ends meet draws nothing.
 */
export function arcPath(cx: number, cy: number, r: number, start: number, end: number): string {
  const sweep = end - start;
  if (!(sweep > 1e-9) || r <= 0) return "";
  if (sweep >= TAU - 1e-9) {
    return `M${round2(cx)} ${round2(cy - r)} A${r} ${r} 0 1 1 ${round2(cx)} ${round2(cy + r)} A${r} ${r} 0 1 1 ${round2(cx)} ${round2(cy - r)} Z`;
  }
  const [x0, y0] = polar(cx, cy, r, start);
  const [x1, y1] = polar(cx, cy, r, end);
  const large = sweep > Math.PI ? 1 : 0;
  return `M${round2(cx)} ${round2(cy)} L${round2(x0)} ${round2(y0)} A${r} ${r} 0 ${large} 1 ${round2(x1)} ${round2(y1)} Z`;
}

function polar(cx: number, cy: number, r: number, angle: number): [number, number] {
  return [cx + r * Math.sin(angle), cy - r * Math.cos(angle)];
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** "42.1%" — a share for a legend; "0%" for nothing, "<0.1%" for a sliver that would read as zero. */
export function sharePercent(fraction: number): string {
  if (!(fraction > 0)) return "0%";
  if (fraction < 0.001) return "<0.1%";
  return `${(fraction * 100).toFixed(1).replace(/\.0$/, "")}%`;
}
